// Saving the logo: SVG, PNG, JPG, favicon pack, social media sizes, brand guide and the complete brand kit (ZIP).
import JSZip from "jszip";
import { buildLogo, outlineText, placeLogo, escapeXml, applyCase } from "./engine";
import { appIconConfig } from "./mockups";
import { cmyk, darken, hexToRgb, lighten, mix, readableOn } from "./color";
import { FONTS } from "./fonts";

export function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const fileSlug = (text) => (String(text || "logo").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40).replace(/^-|-$/g, "") || "logo");
export const withXmlHeader = (svg) => `<?xml version="1.0" encoding="UTF-8"?>\n${svg}`;
export const svgBlob = (svg) => new Blob([withXmlHeader(svg)], { type: "image/svg+xml" });

function loadSvgImage(svg) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("The picture could not be drawn.")); };
    img.src = url;
  });
}

// An SVG drawn onto a canvas of the given size. `fit`: "contain" keeps the whole logo, centred, with `background` around it.
export async function svgToCanvas(svg, width, height, { background = null, fit = "stretch", margin = 0 } = {}) {
  const img = await loadSvgImage(svg);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);
  const ctx = canvas.getContext("2d");
  if (background) { ctx.fillStyle = background; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  if (fit === "contain") {
    const iw = img.naturalWidth || 1;
    const ih = img.naturalHeight || 1;
    const k = Math.min((canvas.width * (1 - margin * 2)) / iw, (canvas.height * (1 - margin * 2)) / ih);
    ctx.drawImage(img, (canvas.width - iw * k) / 2, (canvas.height - ih * k) / 2, iw * k, ih * k);
  } else {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  }
  return canvas;
}

export const canvasBlob = (canvas, type = "image/png", quality = 0.94) => new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("The picture could not be saved."))), type, quality));

// A logo as a picture `width` wide (its height follows the logo's shape).
export async function logoToBlob(logo, width, { type = "image/png", background = null } = {}) {
  const height = (width * logo.height) / logo.width;
  const canvas = await svgToCanvas(logo.svg, width, height, { background: type === "image/jpeg" && !background ? "#ffffff" : background });
  return canvasBlob(canvas, type);
}

// ------------------------------------------------------------------------------------------ favicon
function icoFrom(entries) { // [{ size, bytes }] (PNG data) -> .ico file
  const header = new Uint8Array(6 + entries.length * 16);
  const view = new DataView(header.buffer);
  view.setUint16(2, 1, true);
  view.setUint16(4, entries.length, true);
  let offset = header.length;
  entries.forEach((entry, i) => {
    const at = 6 + i * 16;
    header[at] = entry.size >= 256 ? 0 : entry.size;
    header[at + 1] = entry.size >= 256 ? 0 : entry.size;
    view.setUint16(at + 4, 1, true);
    view.setUint16(at + 6, 32, true);
    view.setUint32(at + 8, entry.bytes.length, true);
    view.setUint32(at + 12, offset, true);
    offset += entry.bytes.length;
  });
  return new Blob([header, ...entries.map((e) => e.bytes)], { type: "image/x-icon" });
}

export function faviconConfig(config) {
  return appIconConfig(config);
}

export async function faviconFiles(config) {
  const mark = buildLogo(faviconConfig(config), { uid: "fv", pad: 0 });
  const sizes = [16, 32, 48, 64, 180, 192, 512];
  const files = {};
  const small = [];
  for (const size of sizes) {
    const blob = await logoToBlob(mark, size);
    files[`favicon-${size}.png`] = blob;
    if (size <= 48) small.push({ size, bytes: new Uint8Array(await blob.arrayBuffer()) });
  }
  files["apple-touch-icon.png"] = files["favicon-180.png"];
  files["favicon.ico"] = icoFrom(small);
  return files;
}

// ------------------------------------------------------------------------------------------ social media pictures
export async function socialFiles(config) {
  const colorful = (mode, extra = {}, uid = "so") => buildLogo({ ...config, ...extra }, { uid, mode, pad: 0 });
  const mark = buildLogo({ ...config, layout: "iconOnly", source: config.source === "none" ? "letters" : config.source, container: config.container === "none" ? "circle" : config.container }, { uid: "sp", pad: 0 });
  const p = config.colors.primary;
  const s = config.colors.secondary;
  const sheet = (w, h, inner, defs = "") => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}"><defs><linearGradient id="bgg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${darken(p, 0.25)}"/><stop offset="1" stop-color="${s === p ? lighten(p, 0.2) : s}"/></linearGradient>${defs}</defs>${inner}</svg>`;
  const files = {};
  const light = colorful("light", { layout: config.layout === "iconOnly" ? "stacked" : config.layout }, "sc");
  files["profile-400x400.png"] = await svgBlobToPng(sheet(400, 400, `<rect width="400" height="400" fill="#ffffff"/>${placeLogo(mark, 40, 40, 320, 320)}`), 400, 400);
  files["cover-1500x500.png"] = await svgBlobToPng(sheet(1500, 500, `<rect width="1500" height="500" fill="url(#bgg)"/><circle cx="1330" cy="80" r="260" fill="#fff" opacity=".07"/><circle cx="120" cy="470" r="200" fill="#fff" opacity=".06"/>${placeLogo(light, 400, 70, 700, 360)}`), 1500, 500);
  files["share-1200x630.png"] = await svgBlobToPng(sheet(1200, 630, `<rect width="1200" height="630" fill="url(#bgg)"/><circle cx="1050" cy="110" r="300" fill="#fff" opacity=".07"/>${placeLogo(light, 250, 90, 700, 450)}`), 1200, 630);
  return files;
}

async function svgBlobToPng(svg, w, h) {
  const canvas = await svgToCanvas(svg, w, h);
  return canvasBlob(canvas);
}

// ------------------------------------------------------------------------------------------ brand guide
export function brandGuideSvg(config) {
  const W = 1600;
  const H = 1100;
  const p = config.colors.primary;
  const s = config.colors.secondary;
  const a = config.colors.accent;
  const full = buildLogo(config, { uid: "bg1", pad: 0 });
  const rev = buildLogo(config, { uid: "bg2", mode: "light", pad: 0 });
  const blk = buildLogo(config, { uid: "bg3", mode: "black", pad: 0 });
  const wht = buildLogo(config, { uid: "bg4", mode: "white", pad: 0 });
  const iconCfg = { ...config, layout: "iconOnly", source: config.source === "none" ? "letters" : config.source };
  const icon = buildLogo(iconCfg, { uid: "bg5", pad: 0 });
  const label = (text, x, y, size = 15, fill = "#6b7280", font = "inter", tracking = 140) => {
    const t = outlineText(font, applyCase(text, "upper"), size, { fill, tracking });
    return `<g transform="translate(${x} ${y})">${t.markup}</g>`;
  };
  const text = (t, x, y, size, fill, font = "inter", tracking = 0) => `<g transform="translate(${x} ${y})">${outlineText(font, t, size, { fill, tracking }).markup}</g>`;
  const swatch = (hex, name, x, y) => {
    const [r, g, b] = hexToRgb(hex);
    const c = cmyk(hex);
    return `<rect x="${x}" y="${y}" width="230" height="120" rx="10" fill="${hex}" stroke="#e5e7eb"/>${text(name, x, y + 142, 20, "#111827", "montserratText")}${text(hex.toUpperCase(), x, y + 172, 16, "#374151", "groteskText")}${text(`RGB ${r} ${g} ${b}`, x, y + 196, 14, "#6b7280", "groteskText")}${text(`CMYK ${c.join(" ")}`, x, y + 216, 14, "#6b7280", "groteskText")}`;
  };
  const dark = darken(p, 0.78);
  const body = `<rect width="${W}" height="${H}" fill="#ffffff"/>`
    + `<rect width="${W}" height="150" fill="${darken(p, 0.6)}"/>${text(`${config.name} - brand guide`, 70, 52, 40, "#ffffff", "montserrat")}${text("Logo, colours and typography", 70, 108, 20, "#cbd5e1", "inter")}`
    + label("Primary logo", 70, 190)
    + `<rect x="70" y="214" width="740" height="360" rx="14" fill="#f8fafc" stroke="#e5e7eb"/>${placeLogo(full, 100, 240, 680, 310)}`
    + label("On dark", 840, 190)
    + `<rect x="840" y="214" width="340" height="170" rx="14" fill="#0f172a"/>${placeLogo(rev, 860, 230, 300, 138)}`
    + `<rect x="1200" y="214" width="330" height="170" rx="14" fill="${p}"/>${placeLogo(wht, 1220, 230, 290, 138)}`
    + label("One colour", 840, 410)
    + `<rect x="840" y="434" width="340" height="140" rx="14" fill="#ffffff" stroke="#e5e7eb"/>${placeLogo(blk, 860, 446, 300, 116)}`
    + `<rect x="1200" y="434" width="330" height="140" rx="14" fill="#f8fafc" stroke="#e5e7eb"/>${placeLogo(icon, 1290, 446, 150, 116)}`
    + label("Colour palette", 70, 626)
    + swatch(p, "Primary", 70, 650) + swatch(s, "Secondary", 330, 650) + swatch(a, "Accent", 590, 650) + swatch(dark, "Ink", 850, 650)
    + label("Typography", 1130, 626)
    + `${text("Aa", 1130, 650, 120, p, config.font)}${text(FONTS[config.font]?.label || "", 1130, 800, 22, "#111827", "montserratText")}${text("Headings and logo name", 1130, 832, 16, "#6b7280", "inter")}`
    + `${text("Aa", 1400, 676, 84, "#374151", config.textFont)}${text(FONTS[config.textFont]?.label || "", 1400, 800, 22, "#111827", "montserratText")}${text("Taglines and body text", 1400, 832, 16, "#6b7280", "inter")}`
    + label("Clear space and minimum size", 70, 930)
    + `<rect x="70" y="954" width="420" height="110" rx="10" fill="#f8fafc" stroke="#e5e7eb"/><rect x="114" y="975" width="332" height="68" fill="none" stroke="${p}" stroke-dasharray="6 5" stroke-width="1.5"/>${placeLogo(full, 130, 983, 300, 52)}`
    + `${text("Keep clear space around the logo equal to the height of its symbol.", 520, 972, 17, "#374151")}${text(`Minimum size: 120 px wide on screen, 25 mm in print.`, 520, 1004, 17, "#374151")}${text("Do not stretch, rotate or recolour the logo. Use the one-colour version when colour is not available.", 520, 1036, 17, "#374151")}`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${[full, rev, blk, wht, icon].map((l) => (l.defs ? `<defs>${l.defs}</defs>` : "")).join("")}${body}</svg>`;
}

// ------------------------------------------------------------------------------------------ the complete kit
export async function brandKitZip(config, mockups = [], progress = () => {}) {
  const zip = new JSZip();
  const slug = fileSlug(config.name);
  const root = zip.folder(`${slug}-brand-kit`);
  const variants = [
    ["primary", { mode: "color" }],
    ["reverse-for-dark-backgrounds", { mode: "light" }],
    ["black", { mode: "black" }],
    ["white", { mode: "white" }],
  ];
  const logoDir = root.folder("logo");
  let step = 0;
  const total = variants.length * 2 + 3 + 4;
  const tick = () => { step += 1; progress(Math.round((step / total) * 100)); };
  for (const [name, opts] of variants) {
    const logo = buildLogo(config, { uid: `k${name.slice(0, 2)}`, ...opts });
    logoDir.file(`${slug}-${name}.svg`, withXmlHeader(logo.svg));
    logoDir.file(`${slug}-${name}.png`, await logoToBlob(logo, 2000));
    tick();
  }
  const mark = buildLogo({ ...config, layout: "iconOnly", source: config.source === "none" ? "letters" : config.source }, { uid: "kic" });
  logoDir.file(`${slug}-symbol.svg`, withXmlHeader(mark.svg));
  logoDir.file(`${slug}-symbol.png`, await logoToBlob(mark, 1200));
  tick();
  const fav = await faviconFiles(config);
  Object.entries(fav).forEach(([n, b]) => root.folder("favicon").file(n, b));
  tick();
  const soc = await socialFiles(config);
  Object.entries(soc).forEach(([n, b]) => root.folder("social-media").file(n, b));
  tick();
  const guide = await svgBlobToPng(brandGuideSvg(config), 1600, 1100);
  root.file("brand-guide.png", guide);
  tick();
  for (const m of mockups) {
    root.folder("mockups").file(`${m.id}.png`, await svgBlobToPng(m.svg, 1200, 800));
    tick();
  }
  const [r, g, b] = [config.colors.primary, config.colors.secondary, config.colors.accent];
  root.file("colors.txt", [`${config.name} - brand colours`, "", `Primary:   ${r}  RGB ${hexToRgb(r).join(" ")}  CMYK ${cmyk(r).join(" ")}`, `Secondary: ${g}  RGB ${hexToRgb(g).join(" ")}  CMYK ${cmyk(g).join(" ")}`, `Accent:    ${b}  RGB ${hexToRgb(b).join(" ")}  CMYK ${cmyk(b).join(" ")}`, "",
    `Fonts: ${FONTS[config.font]?.label} (name), ${FONTS[config.textFont]?.label} (tagline) - both free, open-licence fonts (Google Fonts).`].join("\n"));
  root.file("README.txt", ["BRAND KIT", `${config.name}${config.tagline ? " - " + config.tagline : ""}`, "",
    "logo/          the logo as SVG (vector, text already converted to outlines) and PNG (transparent), in colour, for dark backgrounds, black and white",
    "favicon/       website icons (favicon.ico, PNG sizes, apple-touch-icon)", "social-media/ profile picture, cover picture and link-share picture",
    "mockups/       the logo on cards, shirt, mug, sign, phone, stationery and social page", "brand-guide.png   colours, fonts and logo rules on one page", "",
    "Use SVG for print and for editing in Illustrator, Figma or Inkscape; use PNG for web and documents.", "Made with the logo generator on mraalionline.com."].join("\n"));
  return zip.generateAsync({ type: "blob" });
}

export { escapeXml, mix, readableOn };
